import api from '@/lib/api'
import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed } from 'vue'

export const useStatsStore = defineStore('stats', () => {
  const dashboardStats = ref(/** @type {SwaggerModels.DashboardStats} */ ({}))
  const lastSyncStats = ref(/** @type {SwaggerModels.SyncStats} */ ({}))
  const apiConsumptions = ref({})
  let syncStatusesMap = {}
  const healthSystemMonitoring = ref([])
  const systemHealth = ref({ startDate: undefined, endDate: undefined, anomalies: [] })
  const syncStatsTotalRows = ref(/** @type {SwaggerModels.SyncStats} */ ({}))
  const syncStatsOperations = ref(/** @type {SwaggerModels.SyncStats} */ ({}))

  const healtyStatusMap = {
    Completed: { label: 'OK', color: '#34d399', icon: 'pi pi-check', order: 0 },
    Warning: { label: 'Warning', color: '#fbbf24', icon: 'pi pi-exclamation-circle', order: 2 },
    Paused: { label: 'Paused', color: '#fbbf24', icon: 'pi pi-exclamation-circle', order: 3 },
    Failed: { label: 'KO', color: 'red', icon: 'pi pi-exclamation-triangle', order: 4 },
    New: { label: 'Processing', color: '#60a5fa', icon: 'pi pi-spin pi-spinner', order: 1 },
    Enqueued: { label: 'Processing', color: '#60a5fa', icon: 'pi pi-spin pi-spinner', order: 1 },
    Processing: { label: 'Processing', color: '#60a5fa', icon: 'pi pi-spin pi-spinner', order: 1 },
    null: { label: 'Initializing', color: 'black', icon: 'pi pi-clock', order: 5 }
  }

  const fetchDashboardStats = async () => {
    dashboardStats.value = await api.stats.getDashboard()
    syncStatsTotalRows.value = dashboardStats.value.dailySyncStats
    syncStatsOperations.value = dashboardStats.value.dailySyncStats
  }

  const updateApiConsumption = (value) => {
    addInApiConsumptionMap(value)
  }

  const fetchSyncStats = async (timeframe) => {
    return await api.get(`/hk/v1/stats/record-sync/${timeframe}`)
  }

  const availableSyncStatsObjects = computed(() => {
    const temp = [{ label: 'All', value: 'all' }]
    syncStatsTotalRows.value && syncStatsTotalRows.value.syncStats && Object.keys(syncStatsTotalRows.value.syncStats.reduce((acc, curr) => { acc[curr.object] = true; return acc }, {})).forEach(el => temp.push({ label: el, value: el }))
    return temp
  })

  const initializeSystemStats = async () => {
    syncStatusesMap = (await api.get('/hk/v1/stats/record-sync/lasts')).data
    buildSyncStatuses()
  }

  const refreshSyncStatusesMap = (obj, status) => {
    syncStatusesMap[obj] = status
    buildSyncStatuses()
  }

  const buildSyncStatuses = () => {
    const occurrences = Object.values(syncStatusesMap).reduce(function (acc, curr) {
      ['New', 'Enqueued', 'Processing'].includes(curr) ? acc.Processing ? ++acc.Processing : acc.Processing = 1 : acc[curr] ? ++acc[curr] : acc[curr] = 1
      return acc
    }, {})

    let tot = 0
    const values = (Object.keys(occurrences).map(status => {
      tot += occurrences[status]
      return { ...healtyStatusMap[status], value: occurrences[status] }
    })).sort((a, b) => a.order - b.order)
    const lastIndex = values.length - 1
    let acc = 0
    healthSystemMonitoring.value = values.map((el, i) => {
      const perc = i === lastIndex ? 100 - acc : Math.round(el.value * 100 / tot)
      acc += perc
      el.value = perc
      return el
    })
  }

  const fetchAnomalies = async () => {
    if (!systemHealth.value.startDate) {
      let startDate = new Date()
      let endDate = new Date()
      startDate = new Date(startDate.setDate(startDate.getDate() - 7))
      endDate = new Date(endDate.setMinutes(endDate.getMinutes() + 30))
      const response = await api.anomalies.getAnomalies({
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString()
      })
      systemHealth.value = {
        startDate,
        endDate,
        anomalies: response.map(el => {
          el.createddate = new Date(el.createddate)
          el.duration.milliseconds = Math.round(el.duration.milliseconds)
          // @ts-ignore
          const duration = (new Intl.DurationFormat('en', { style: 'narrow' }).format(el.duration)).split(' ')
          return { ...el, formattedDuration: duration[0] + ' ' + (duration[1] || '') }
        })
      }
    }
  }
  const addInApiConsumptionMap = (el) => {
    const d = new Date(el.rangedate)
    const hour = d.getHours()
    const date = d.setHours(0, 0, 0, 0)
    if (!apiConsumptions.value[date]) {
      apiConsumptions.value[date] = {}
    }
    apiConsumptions.value[date][hour] = el.total
  }

  const fetchApiConsumptions = async () => {
    const resp = await api.get('/hk/v1/stats/api-consumptions')
    resp.data.forEach(el => addInApiConsumptionMap(el))
  }
  return {
    dashboardStats,
    lastSyncStats,
    healthSystemMonitoring,
    syncStatsTotalRows,
    syncStatsOperations,
    systemHealth,
    availableSyncStatsObjects,
    apiConsumptions,
    fetchDashboardStats,
    fetchSyncStats,
    fetchAnomalies,
    fetchApiConsumptions,
    updateApiConsumption,
    initializeSystemStats,
    refreshSyncStatusesMap
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useStatsStore, import.meta.hot))
}
